/**
 * Coral -> red severity heat ramp for the DQ Workbench.
 *
 * One source so the FP Grid cells, the Trending rate bars and any other
 * severity surface read on the SAME scale. Distinct from `agingRamp.ts`
 * (which is teal -> amber -> red for "older = staler") - this ramp is purely
 * about volume/severity intensity, low (warm cream) -> high (deep red).
 *
 * Per the approved design language:
 *   #FBEDE7 (low) -> #F9E0D6 -> #F4C7B8 -> #EE9B7A -> #A32D2D (high)
 */
export const DQ_HEAT_RAMP = [
  '#FBEDE7',
  '#F9E0D6',
  '#F4C7B8',
  '#EE9B7A',
  '#A32D2D',
] as const

/** Empty/zero cells render plain - no ink, just the secondary border colour. */
export const DQ_HEAT_EMPTY = 'transparent'

/**
 * Background colour for a cell value relative to the column/grid max.
 *
 * Buckets the ratio into the five-stop ramp so the eye finds hot cells
 * without reading numbers. ``value <= 0`` returns transparent (empty cell).
 * ``max <= 0`` (whole grid empty) also returns transparent.
 */
export const dqHeatBackground = (value: number, max: number): string => {
  if (!value || value <= 0 || max <= 0) return DQ_HEAT_EMPTY
  const ratio = value / max
  if (ratio <= 0.2) return DQ_HEAT_RAMP[0]
  if (ratio <= 0.4) return DQ_HEAT_RAMP[1]
  if (ratio <= 0.6) return DQ_HEAT_RAMP[2]
  if (ratio <= 0.8) return DQ_HEAT_RAMP[3]
  return DQ_HEAT_RAMP[4]
}

/**
 * Readable text colour for a heat background - white on the two darkest
 * stops, charcoal otherwise. Keeps cell numbers legible across the ramp.
 */
export const dqHeatText = (value: number, max: number): string => {
  if (!value || value <= 0 || max <= 0) return '#9e9e9e'
  const ratio = value / max
  return ratio > 0.6 ? '#FFFFFF' : '#333333'
}
